import { Sparkles, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/** Shape returned by /api/game-insights (and stored as GameInsight). */
export interface InsightCardData {
  id?: string;
  subjectPlayerId?: string;
  subjectPlayerName: string;
  type: string;
  inningLabel?: string;
  prose: string;
  benchmark?: { value: string; label: string; comparison: string };
  dismissed?: boolean;
}

/**
 * One coaching insight, modeled on MLB's Scout Insights cards (player name in
 * blue, one fact, a "vs level avg" chip), adapted to Dugout's dark theme.
 * Prose is always the suggestive bench-coach voice — generated upstream.
 */
export function GameInsightCard({
  insight, onDismiss, onPlayerTap,
}: {
  insight: InsightCardData;
  onDismiss?: () => void;
  onPlayerTap?: (playerId?: string) => void;
}) {
  // legacy-invert: this card is authored against the inverted palette (see
  // src/index.css) but is rendered inside light screens (App.tsx, the game
  // import modal), so it scopes the inversion to itself.
  return (
    <div className="legacy-invert bg-zinc-900 rounded-2xl border border-zinc-800 p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onPlayerTap?.(insight.subjectPlayerId)}
            disabled={!onPlayerTap || !insight.subjectPlayerId}
            className={cn(
              'text-sm font-black tracking-tight',
              insight.subjectPlayerId && onPlayerTap ? 'text-sky-400 hover:text-sky-300' : 'text-white',
            )}
          >
            {insight.subjectPlayerName}
          </button>
          <p className="text-sm text-zinc-300 leading-relaxed mt-1">{insight.prose}</p>

          {insight.benchmark && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-zinc-800/70">
              <span className="text-[11px] font-black text-white">{insight.benchmark.value}</span>
              <span className="text-[10px] font-bold text-zinc-500">
                {insight.benchmark.label} {insight.benchmark.comparison}
              </span>
            </div>
          )}

          <div className="mt-2">
            <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-800 text-[9px] font-black uppercase tracking-widest text-zinc-400">
              {insight.subjectPlayerName}
            </span>
          </div>
        </div>

        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors shrink-0" title="Dismiss">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Group cards under MLB-style inning headers ("Top 3rd"), ungrouped last. */
export function groupInsightsByInning(insights: InsightCardData[]): Array<{ label: string | null; items: InsightCardData[] }> {
  const groups = new Map<string | null, InsightCardData[]>();
  for (const ins of insights) {
    const k = ins.inningLabel || null;
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ins);
  }
  return [...groups.entries()].map(([label, items]) => ({ label, items }));
}
